import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ListItemButton from "@mui/material/ListItemButton";

import { fToNow } from "src/utils/format-time";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { markAsRead } from "src/store/notification/notification.slice";
import { paths } from "src/routes/paths";
import type { Notification } from "src/store/types";

// ----------------------------------------------------------------------

export type NotificationItemProps = {
  notification: Notification;
  onClose: () => void;
};

export function NotificationItem({
  notification,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleClick = () => {
    if (!notification.isNotificationRead) {
      dispatch(markAsRead(notification.id));
    }

    onClose();

    const ocid = notification.openCall?.ocid;
    if (ocid) {
      navigate(paths.dashboard.openCallAuditions(ocid));
      return;
    }

    navigate(paths.dashboard.openCall);
  };

  const renderText = () => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          mb: 0.5,
          fontWeight: notification.isNotificationRead ? "normal" : "medium",
          color: notification.isNotificationRead
            ? "text.secondary"
            : "text.primary",
          lineHeight: 1.4,
        }}
      >
        {notification.body}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          typography: "caption",
          color: "text.disabled",
        }}
      >
        <span>{fToNow(notification.createdAt)}</span>
        <Box
          component="span"
          sx={{
            width: 2,
            height: 2,
            borderRadius: "50%",
            bgcolor: "currentColor",
          }}
        />
        <span style={{ textTransform: "capitalize" }}>
          {notification.notificationType.replace(/([A-Z])/g, " $1").trim()}
        </span>
      </Box>
    </Box>
  );

  const renderUnReadBadge = () =>
    !notification.isNotificationRead && (
      <Box
        sx={{
          top: 16,
          right: 16,
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: "info.main",
          position: "absolute",
        }}
      />
    );

  return (
    <ListItemButton
      disableRipple
      onClick={handleClick}
      sx={[
        (theme) => ({
          p: 2,
          width: "100%",
          position: "relative",
          alignItems: "flex-start",
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
          bgcolor: notification.isNotificationRead
            ? "transparent"
            : "action.hover",
          "&:hover": {
            bgcolor: notification.isNotificationRead
              ? "action.hover"
              : "action.selected",
          },
        }),
      ]}
    >
      {renderUnReadBadge()}
      {renderText()}
    </ListItemButton>
  );
}
